<template>
  <div class="page">
    <!-- ========== 主页内容 ========== -->
    <div v-show="activeTab === 'home'">
      <!-- 第一行：顶部图片横幅 — 图片链接来源于接口 imageUrls 字段，多张自动循环，无数据时兜底用本地横幅 -->
      <img
        v-if="imageUrls.length"
        class="banner"
        :src="imageUrls[currentImg]"
        alt="幸运号码活动横幅"
      />
      <img v-else class="banner" :src="bannerImg" alt="幸运号码活动横幅"/>

      <!-- 第二行：通知栏 — 小喇叭图标 + 滚动通知内容（来源接口 noticeContent） -->
      <div v-if="noticeText" class="notice-bar">
        <svg class="notice-icon" viewBox="0 0 24 24">
          <path d="M3 10v4a1 1 0 0 0 1 1h3l4 4V5L7 9H4a1 1 0 0 0-1 1z"/>
          <path d="M16 8a5 5 0 0 1 0 8" stroke-dasharray="2 2"/>
          <path d="M19 5a9 9 0 0 1 0 14" stroke-dasharray="2 2"/>
        </svg>
        <div class="notice-scroll">
          <span class="notice-text">{{ noticeText }}</span>
        </div>
      </div>

      <!-- 第三行：操作栏（号码管理 / 彩票管理 / 兑奖管理 / 意见反馈） -->
      <div class="operate-bar">
        <div
            v-for="item in operateList"
            :key="item.name"
            class="operate-item"
            @click="handleOperate(item)"
        >
          <!-- 每个操作的图标 -->
          <div class="operate-icon" :style="{ background: item.color }">
            <!-- 号码管理图标 -->
            <svg v-if="item.icon === 'list'" viewBox="0 0 24 24">
              <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/>
            </svg>
            <!-- 彩票管理图标 -->
            <svg v-else-if="item.icon === 'ticket'" viewBox="0 0 24 24">
              <path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2 2 2 0 0 0 0 4 2 2 0 0 1-2 2H5a2 2 0 0 1-2-2 2 2 0 0 0 0-4z"/>
              <path d="M14 6v12" stroke-dasharray="2 2"/>
            </svg>
            <!-- 兑奖管理图标 -->
            <svg v-else-if="item.icon === 'gift'" viewBox="0 0 24 24">
              <path d="M20 12v9H4v-9M2 7h20v5H2zM12 22V7M12 7S10.5 3 8 3 5 5 5 7h7zM12 7s1.5-4 4-4 3 2 3 4h-7z"/>
            </svg>
            <!-- 意见反馈图标 -->
            <svg v-else viewBox="0 0 24 24">
              <path
                  d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
            </svg>
          </div>
          <span class="operate-text">{{ item.name }}</span>
        </div>
      </div>

      <!-- 最新开奖：四种彩票最新一期号码，自动横向滑动轮播 -->
      <div class="last-draw">
        <div class="last-draw-head">
          <span class="last-draw-title">最新开奖</span>
          <!-- 暂停/播放：点击切换轮播是否自动滑动；轮播进行中显示暂停图标，暂停中显示播放图标 -->
          <span class="last-draw-more" @click="toggleAutoSlide">
            <!-- 暂停图标：两根竖条 -->
            <svg v-if="autoSlide" viewBox="0 0 24 24">
              <rect x="6" y="5" width="4" height="14" rx="1.5" />
              <rect x="14" y="5" width="4" height="14" rx="1.5" />
            </svg>
            <!-- 播放图标：实心三角形 -->
            <svg v-else viewBox="0 0 24 24">
              <path d="M8 5.5v13l11-6.5z" />
            </svg>
          </span>
        </div>

        <!-- 轮播视口：超出部分隐藏 -->
        <div class="slider-viewport">
          <!-- 轨道：整体左移实现滑动，每一页宽度 100% -->
          <div
              class="slider-track"
              :style="{ transform: 'translateX(-' + currentSlide * 100 + '%)' }"
          >
            <div
                v-for="item in lastDrawList"
                :key="item.lotteryType"
                class="slider-slide"
            >
              <!-- 彩票类型名称 + 期号 + 开奖时间 -->
              <div class="slide-meta">
                <span class="slide-type">{{ typeLabelMap[item.lotteryType] || item.lotteryType }}</span>
                <span class="slide-expect">
                  第 {{ item.drawNumIssue }} 期
                  <span class="slide-time" v-if="item.drawNumTime">{{ formatDrawTime(item.drawNumTime) }}</span>
                </span>
              </div>
              <!-- 开奖时间补充说明（如：每日 21:30 开奖），接口有值才展示 -->
              <div v-if="item.drawNumTimeInfo" class="slide-extend">{{ item.drawNumTimeInfo }}</div>
              <!-- 号码球：drawNumDTO.redNum 主号码红色，blueNum 辅助号码蓝色；自动换行 -->
              <div class="slide-nums">
                <span
                    v-for="(n, i) in mainNums(item.drawNumDTO)"
                    :key="'m' + i"
                    class="mini-ball"
                >{{ n }}</span>
                <template v-if="extraNums(item.drawNumDTO).length">
                  <span class="mini-plus">+</span>
                  <span
                      v-for="(n, i) in extraNums(item.drawNumDTO)"
                      :key="'e' + i"
                      class="mini-ball blue"
                  >{{ n }}</span>
                </template>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部小圆点：可点击直接跳到某一页 -->
        <div class="slider-dots">
          <span
              v-for="(item, i) in lastDrawList"
              :key="'dot' + item.lotteryType"
              class="dot"
              :class="{ active: currentSlide === i }"
              @click="jumpSlide(i)"
          ></span>
        </div>
      </div>
    </div>

    <!-- ========== 我的页面内容 ========== -->
    <div v-show="activeTab === 'mine'" class="mine-panel">
      <div class="mine-header">
        <div class="mine-avatar">
          <!-- 用户头像图标 -->
          <svg viewBox="0 0 24 24">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
        </div>
        <div class="mine-name">幸运用户：{{ userName }}</div>
      </div>

      <div class="mine-menu">
        <div class="mine-menu-item">我的号码</div>
        <div class="mine-menu-item" @click="goGameRules">玩法规则</div>
        <div class="mine-menu-item" @click="goBuyGuide" >购票指南</div>
        <div class="mine-menu-item">设置</div>
      </div>

      <button class="logout-btn" @click="handleLogout">退出登录</button>
    </div>

    <!-- ========== 最下面：底部标签栏（主页 / 我的） ========== -->
    <div class="tab-bar">
      <div
          class="tab-item"
          :class="{ active: activeTab === 'home' }"
          @click="activeTab = 'home'"
      >
        <!-- 主页图标（房子） -->
        <svg viewBox="0 0 24 24">
          <path d="M3 9.5 12 3l9 6.5V20a1 1 0 0 1-1 1h-5v-7h-6v7H4a1 1 0 0 1-1-1V9.5z"/>
        </svg>
        <span>主页</span>
      </div>

      <div
          class="tab-item"
          :class="{ active: activeTab === 'mine' }"
          @click="activeTab = 'mine'"
      >
        <!-- 我的图标（小人） -->
        <svg viewBox="0 0 24 24">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
          <circle cx="12" cy="7" r="4"/>
        </svg>
        <span>我的</span>
      </div>
    </div>
  </div>
</template>

<script>
// 在顶部直接 import 图片
import bannerImgSrc from '@/assets/image_419090010192399.png'
// 导入最新开奖号码接口
import {getLastDrawNumList} from '@/api/lottery'
// 导入首页展示信息接口（图片 + 通知公告）
import {getDisplayInfo} from '@/api/system'

export default {
  name: 'HomeView',
  data() {
    return {
      // 当前选中的底部标签：home=主页，mine=我的
      activeTab: 'home',

      // 顶部横幅图片（横图，由图片接口生成）
      bannerImg: bannerImgSrc,

      // ===== 首页展示信息（来源 /system/display 接口） =====
      imageUrls: [],         // 首页展示图片链接数组
      noticeContent: [],     // 通知公告内容数组
      currentImg: 0,         // 当前展示的图片索引（多张时自动循环切换）
      imgTimer: null,        // 图片自动轮播定时器（离开页面时要清掉）

      // ===== 最新开奖轮播相关 =====
      lastDrawList: [],   // 四种彩票的最新一期数据
      currentSlide: 0,   // 当前显示第几页（从 0 开始）
      slideTimer: null,  // 自动滑动的定时器（离开页面时要清掉）
      autoSlide: true,   // 轮播是否自动滑动：点暂停按钮后置 false
      // 彩票类型编码 -> 中文名称
      typeLabelMap: {
        klb: '快乐8',
        ssq: '双色球',
        qlc: '七乐彩',
        fcsd: '福彩3D',
        dlt: '大乐透',
        qxc: '七星彩',
        pls: '排列三',
        plw: '排列五'
      },

      // 操作栏的四个功能（key 用于点击时判断跳转到哪个页面）
      operateList: [
        {name: '号码管理', icon: 'list', color: '#667eea', key: 'numManage'},
        {name: '彩票管理', icon: 'ticket', color: '#f56c6c', key: 'lotteryMange'},
        {name: '兑奖管理', icon: 'gift', color: '#e6a23c', key: 'prizeManage'},
        {name: '意见反馈', icon: 'message', color: '#67c23a', key: 'feedback'}
      ]
    }
  },
  computed: {
    // 当前登录用户名：登录时存入 localStorage，退出时清除
    userName() {
      return localStorage.getItem('userName') || ''
    },
    // 通知文本：多条公告用空格分隔拼接成一条滚动文本
    noticeText() {
      return this.noticeContent.filter(Boolean).join('　　')
    }
  },
  // 页面创建后：请求展示信息和最新开奖数据，并开启自动滑动
  created() {
    this.fetchDisplayInfo()
    this.fetchLastDraw()
    this.startAutoSlide()
  },
  // 页面销毁前：清除定时器，避免后台还在跑
  beforeDestroy() {
    this.stopImgTimer()
    this.stopAutoSlide()
  },
  methods: {
    // 请求首页展示信息（图片链接 + 通知公告）
    async fetchDisplayInfo() {
      try {
        const res = await getDisplayInfo()
        // 后端返回：{ code: "200", data: { imageUrls: [...], noticeContent: [...] }, msg, success }
        if ((res.code === '200' || res.success) && res.data) {
          this.imageUrls = res.data.imageUrls || []
          this.noticeContent = res.data.noticeContent || []
          this.startImgTimer()
        }
      } catch (err) {
        console.error('获取首页展示信息失败：', err)
      }
    },

    // 开启图片自动轮播：每 4 秒切到下一张，多张时循环
    startImgTimer() {
      this.stopImgTimer()
      if (this.imageUrls.length > 1) {
        this.imgTimer = setInterval(() => {
          this.currentImg = (this.currentImg + 1) % this.imageUrls.length
        }, 4000)
      }
    },

    // 停止图片自动轮播
    stopImgTimer() {
      if (this.imgTimer) {
        clearInterval(this.imgTimer)
        this.imgTimer = null
      }
    },

    // 请求各彩票类型的最新开奖号码
    async fetchLastDraw() {
      try {
        const res = await getLastDrawNumList()
        // 后端返回：{ code: "200", data: [...], msg, success }
        if (res.code === '200' || res.success) {
          this.lastDrawList = res.data || []
        }
      } catch (err) {
        console.error('获取最新开奖号码失败：', err)
      }
    },

    // 开启自动滑动：每 5 秒切到下一页，到最后一页后回到第一页循环
    startAutoSlide() {
      this.slideTimer = setInterval(() => {
        if (this.lastDrawList.length === 0) return
        this.currentSlide =
            (this.currentSlide + 1) % this.lastDrawList.length
      }, 5000)
    },

    // 停止自动滑动
    stopAutoSlide() {
      if (this.slideTimer) {
        clearInterval(this.slideTimer)
        this.slideTimer = null
      }
    },

    // 暂停/播放：点一下暂停自动滑动，再点一下恢复
    toggleAutoSlide() {
      this.autoSlide = !this.autoSlide
      if (this.autoSlide) {
        this.startAutoSlide()
      } else {
        this.stopAutoSlide()
      }
    },

    // 点击小圆点：直接跳到指定页
    jumpSlide(index) {
      this.currentSlide = index
    },

    // 主号码：取开奖号码对象 drawNumDTO 里的 redNum，按逗号拆成数组
    mainNums(drawNumDTO) {
      return (drawNumDTO && drawNumDTO.redNum ? drawNumDTO.redNum : '')
          .split(',').filter(Boolean)
    },
    // 辅助号码：取 drawNumDTO 里的 blueNum（如双色球蓝球），没有则为空数组
    extraNums(drawNumDTO) {
      return (drawNumDTO && drawNumDTO.blueNum ? drawNumDTO.blueNum : '')
          .split(',').filter(Boolean)
    },

    // 开奖时间格式化：后端返回 "2026-09-30 21:30:00"，精简成 "09-30 21:30"
    formatDrawTime(time) {
      if (!time) return ''
      const parts = String(time).split(' ')
      const date = parts[0] ? parts[0].slice(5) : ''    // 2026-09-30 -> 09-30
      const hm = parts[1] ? parts[1].slice(0, 5) : ''  // 21:30:00 -> 21:30
      return (date || '') + (hm ? ' ' + hm : '')
    },

    // 跳转到玩法规则页
    goGameRules() {
      this.$router.push('/game-rules')
    },

    // 跳转到购票指南页
    goBuyGuide() {
      this.$router.push('/buy-guide')
    },

    // 点击操作栏：号码管理、彩票管理、兑奖管理、意见反馈跳转对应页面
    handleOperate(item) {
      if (item.key === 'numManage') {
        // 号码管理：进入号码管理页
        this.$router.push('/num-manage')
      } else if (item.key === 'lotteryMange') {
        // 彩票管理：进入选号方式页（手动输入 / 随机生成/模型推荐）
        this.$router.push('/lottery-mange')
      } else if (item.key === 'prizeManage') {
        // 兑奖管理：进入兑奖管理页（手动/预约兑奖 + 待兑奖列表）
        this.$router.push('/prize-manage')
      } else if (item.key === 'feedback') {
        // 意见反馈：进入反馈列表页（查看反馈 + 新增反馈）
        this.$router.push('/feedback')
      } else {
        alert(`「${item.name}」功能开发中`)
      }
    },

    // 退出登录：清除 token 并回到登录页
    handleLogout() {
      localStorage.removeItem('token')
      localStorage.removeItem('userRole')
      localStorage.removeItem('userName')
      this.$router.push('/login')
    }
  }
}
</script>

<style scoped>
/* 页面整体：手机门户风格，限制最大宽度并居中，方便电脑上预览 */
.page {
  width: 100%;
  max-width: 480px;
  min-height: 100%;
  margin: 0 auto;
  background: #f5f6fa;
  position: relative;
  padding-bottom: 60px; /* 给底部固定标签栏留位置 */
}

/* ===== 通知栏：小喇叭图标 + 横向滚动通知 ===== */
.notice-bar {
  display: flex;
  align-items: center;
  height: 36px;
  padding: 0 10px;
  background: #fff;
  overflow: hidden;
}

.notice-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  fill: none;
  stroke: #f56c6c;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  margin-right: 8px;
}

.notice-scroll {
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
}

.notice-text {
  display: inline-block;
  font-size: 13px;
  color: #606266;
  padding-left: 100%; /* 从右侧滑入 */
  animation: notice-marquee 18s linear infinite;
}

@keyframes notice-marquee {
  0%   { transform: translateX(0); }
  100% { transform: translateX(-100%); }
}

/* ===== 顶部横幅图片 ===== */
.banner {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9; /* 按宽高比显示，避免图片加载时跳动 */
  object-fit: cover;
}

/* ===== 操作栏 ===== */
.operate-bar {
  display: flex;
  margin: 12px;
  padding: 16px 8px;
  background: #fff;
  border-radius: 12px;
}

.operate-item {
  flex: 1; /* 四个操作平均分配宽度 */
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
}

/* 圆形图标背景 */
.operate-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.operate-icon svg {
  width: 22px;
  height: 22px;
  fill: none;
  stroke: #fff;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.operate-text {
  font-size: 13px;
  color: #333;
}

/* ===== 最新开奖轮播 ===== */
.last-draw {
  margin: 0 12px 12px;
  padding: 14px;
  background: #fff;
  border-radius: 12px;
}

.last-draw-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.last-draw-title {
  font-size: 15px;
  font-weight: bold;
  color: #303133;
}

/* 暂停/播放图标按钮：flex 布局居中，扩大点击热区方便手机点按 */
.last-draw-more {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  cursor: pointer;
}

.last-draw-more svg {
  width: 18px;
  height: 18px;
  fill: #667eea;
}

/* 轮播视口 */
.slider-viewport {
  overflow: hidden; /* 只显示当前一页，其余裁掉 */
}

/* 轨道：所有页横向排成一排，加过渡动画实现平滑滑动 */
.slider-track {
  display: flex;
  transition: transform 0.5s ease;
}

/* 每一页固定占视口的 100% 宽，不会被压缩 */
.slider-slide {
  flex: 0 0 100%;
}

.slide-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.slide-type {
  font-size: 14px;
  font-weight: bold;
  color: #f56c6c;
}

.slide-expect {
  font-size: 12px;
  color: #909399;
}

/* 开奖时间：与期号同行，颜色再浅一点做区分 */
.slide-time {
  margin-left: 6px;
  color: #c0c4cc;
}

/* 开奖时间补充说明（drawNumTimeInfo）：期号行下方独立一行小字 */
.slide-extend {
  margin-bottom: 8px;
  font-size: 12px;
  color: #909399;
}

/* 号码球区域：允许换行（快乐8一期有20个号码） */
.slide-nums {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px;
}

/* 小号号码球 */
.mini-ball {
  min-width: 24px;
  height: 24px;
  padding: 0 4px;
  border-radius: 12px;
  background: #f56c6c;
  color: #fff;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.mini-ball.blue {
  background: #409eff;
}

.mini-plus {
  color: #909399;
  font-size: 12px;
  font-weight: bold;
}

/* 底部小圆点 */
.slider-dots {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 10px;
}

.dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #dcdfe6;
  cursor: pointer;
}

.dot.active {
  width: 16px; /* 选中的点变长 */
  border-radius: 3px;
  background: #667eea;
}

/* ===== 我的页面 ===== */
.mine-panel {
  padding: 12px;
}

.mine-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 28px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.mine-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
}

.mine-avatar svg {
  width: 36px;
  height: 36px;
  fill: none;
  stroke: #fff;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.mine-name {
  color: #fff;
  font-size: 17px;
}

.mine-menu {
  margin-top: 12px;
  background: #fff;
  border-radius: 12px;
}

.mine-menu-item {
  padding: 15px 16px;
  font-size: 14px;
  color: #333;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
}

.mine-menu-item:last-child {
  border-bottom: none;
}

.logout-btn {
  width: 100%;
  margin-top: 20px;
  height: 42px;
  border: none;
  border-radius: 8px;
  background: #f56c6c;
  color: #fff;
  font-size: 15px;
  cursor: pointer;
}

/* ===== 底部标签栏：固定在底部 ===== */
.tab-bar {
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%); /* 配合最大宽度居中 */
  width: 100%;
  max-width: 480px;
  height: 56px;
  background: #fff;
  border-top: 1px solid #ebeef5;
  display: flex;
}

.tab-item {
  flex: 1; /* 主页、我的各占一半 */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #909399;
  font-size: 11px;
  cursor: pointer;
}

.tab-item svg {
  width: 22px;
  height: 22px;
  fill: none;
  stroke: #909399;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  margin-bottom: 2px;
}

/* 选中状态：文字和图标变成主题色 */
.tab-item.active {
  color: #667eea;
}

.tab-item.active svg {
  stroke: #667eea;
}
</style>
